import React, { useMemo } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Checkbox,
  FormControlLabel,
  Switch,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

const menuKey = (m) => String(m.menuId ?? m.menu_id);

const productOf = (m) => String(m.productCode || m.product_code || '').toUpperCase();

const isSharedMenu = (m) => {
  const code = productOf(m);
  return !code || code === 'PLATFORM' || m.licenseRequired === false || m.license_required === false;
};

/**
 * Expandable products grid on License: each product row expands to that product's menus only.
 * Hub enabled → only Hub menus inside Hub. No separate Assign-menus screen.
 */
const LicenseProductsMenusGrid = ({
  productOptions = [],
  productState = {},
  onProductChange,
  menus = [],
  selectedMenuIds,
  onMenuToggle,
  onToggleProductMenus,
  disabled = false,
}) => {
  const menusByProduct = useMemo(() => {
    const map = {};
    (productOptions || []).forEach((p) => {
      map[p.code] = [];
    });
    (menus || []).forEach((m) => {
      if (isSharedMenu(m)) return;
      const code = productOf(m);
      if (!map[code]) map[code] = [];
      map[code].push(m);
    });
    Object.keys(map).forEach((code) => {
      map[code].sort(
        (a, b) =>
          (a.displayOrder ?? a.display_order ?? 0) - (b.displayOrder ?? b.display_order ?? 0) ||
          String(a.menuName || a.menu_name || '').localeCompare(String(b.menuName || b.menu_name || ''))
      );
    });
    return map;
  }, [menus, productOptions]);

  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        overflow: 'hidden',
      }}
    >
      <Box
        display="flex"
        alignItems="center"
        px={1.5}
        py={0.75}
        sx={{ bgcolor: 'action.hover', borderBottom: '1px solid', borderColor: 'divider' }}
      >
        <Typography variant="caption" fontWeight={600} sx={{ flex: 1 }}>
          Product
        </Typography>
        <Typography variant="caption" fontWeight={600} sx={{ width: 88, textAlign: 'center' }}>
          Enabled
        </Typography>
        <Typography variant="caption" fontWeight={600} sx={{ width: 72, textAlign: 'right' }}>
          Menus
        </Typography>
      </Box>

      {(productOptions || []).map((p) => {
        const enabled = !!productState[p.code];
        const productMenus = menusByProduct[p.code] || [];
        const selectedCount = productMenus.filter((m) => selectedMenuIds.has(menuKey(m))).length;

        return (
          <Accordion
            key={p.code}
            disableGutters
            elevation={0}
            sx={{
              '&:before': { display: 'none' },
              borderBottom: '1px solid',
              borderColor: 'divider',
              '&.Mui-expanded': { bgcolor: 'transparent' },
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon fontSize="small" />}
              sx={{
                minHeight: 44,
                px: 1.5,
                '& .MuiAccordionSummary-content': { my: 0.5, alignItems: 'center', gap: 1 },
              }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" fontWeight={600} noWrap>
                  {p.name || p.code}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {p.code}
                </Typography>
              </Box>
              <Box
                onClick={(e) => e.stopPropagation()}
                onFocus={(e) => e.stopPropagation()}
                sx={{ width: 88, display: 'flex', justifyContent: 'center' }}
              >
                <Switch
                  size="small"
                  checked={enabled}
                  disabled={disabled}
                  onChange={(e) => onProductChange?.(p.code, e.target.checked)}
                />
              </Box>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ width: 72, textAlign: 'right', pr: 0.5 }}
              >
                {enabled ? `${selectedCount}/${productMenus.length}` : '—'}
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 0, pb: 1.5, px: 2 }}>
              {!enabled ? (
                <Typography variant="body2" color="text.secondary">
                  Enable {p.name || p.code} to assign its menus.
                </Typography>
              ) : productMenus.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No master menus for this product yet.
                </Typography>
              ) : (
                <Box>
                  <Box display="flex" justifyContent="flex-end" mb={0.5}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          size="small"
                          disabled={disabled}
                          checked={selectedCount === productMenus.length && productMenus.length > 0}
                          indeterminate={selectedCount > 0 && selectedCount < productMenus.length}
                          onChange={(e) => onToggleProductMenus?.(p.code, productMenus, e.target.checked)}
                        />
                      }
                      label={<Typography variant="caption">All menus</Typography>}
                      sx={{ mr: 0 }}
                    />
                  </Box>
                  {productMenus.map((m) => {
                    const id = menuKey(m);
                    const name = m.menuName || m.menu_name || m.menuCode || m.menu_code || id;
                    const level = m.menuLevel ?? m.menu_level ?? 1;
                    return (
                      <FormControlLabel
                        key={id}
                        control={
                          <Checkbox
                            size="small"
                            disabled={disabled}
                            checked={selectedMenuIds.has(id)}
                            onChange={() => onMenuToggle?.(id)}
                          />
                        }
                        label={
                          <Typography variant="body2" sx={{ pl: level > 1 ? 2 : 0 }}>
                            {name}
                          </Typography>
                        }
                        sx={{ display: 'flex', ml: 0, width: '100%' }}
                      />
                    );
                  })}
                </Box>
              )}
            </AccordionDetails>
          </Accordion>
        );
      })}
    </Box>
  );
};

export { menuKey, productOf, isSharedMenu };
export default LicenseProductsMenusGrid;
